import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { DollarSign, Send, X, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MakeOfferModal({ 
  isOpen, 
  onClose, 
  product, 
  selectedVariant,
  token,
  onOfferSubmitted,
  existingOffer // Pass existing offer if user already has one
}) {
  // Slider value represents the percentage (75-100, default 99)
  const [offerPercent, setOfferPercent] = useState(99);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setOfferPercent(99);
      setCustomAmount('');
      setUseCustom(false);
      setError('');
      setSubmitted(false);
    }
  }, [isOpen]);

  // Calculate offer amount — use variant price when available (variants can have per-variant pricing)
  const effectivePrice = selectedVariant?.price ?? product?.price ?? 0;
  const minOffer = effectivePrice ? effectivePrice * 0.75 : 0;
  const offerAmount = useCustom 
    ? parseFloat(customAmount) || 0 
    : (effectivePrice ? (effectivePrice * offerPercent / 100) : 0);
  const savings = effectivePrice ? (effectivePrice - offerAmount) : 0;
  const discountPercent = effectivePrice ? ((1 - offerAmount / effectivePrice) * 100) : 0;

  const handleSubmit = async () => {
    setError('');
    
    if (!effectivePrice) {
      setError('Product price not available');
      return;
    }

    if (offerAmount < minOffer) {
      setError(`Minimum offer is $${minOffer.toFixed(2)} (75%)`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { amount: parseFloat(offerAmount.toFixed(2)) };
      if (selectedVariant?.id) {
        payload.variant_id = selectedVariant.id;
      }
      await axios.post(
        `${API}/products/${product.id}/offers`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Offer submitted!');
      setSubmitted(true);
      onOfferSubmitted?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit offer';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOfferPercent(99);
    setCustomAmount('');
    setUseCustom(false);
    setError('');
    onClose();
  };

  // Show "reviewing" state if user already has an offer or just submitted
  // Show "accepted" state if offer was accepted
  // Show "declined" state if offer negotiation ended
  if (existingOffer || submitted) {
    const isAccepted = existingOffer?.status === 'accepted';
    const isDeclined = existingOffer?.status === 'declined' || existingOffer?.status === 'declined_final';
    
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md bg-zinc-950 border-zinc-800 p-0 [&>button]:absolute [&>button]:right-3 [&>button]:top-3 [&>button]:p-2 [&>button]:bg-zinc-800 [&>button]:rounded [&>button]:opacity-100 [&>button]:hover:bg-zinc-700">
          <div className="p-6 text-center space-y-4 pt-10">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              isAccepted ? 'bg-green-500/20' : isDeclined ? 'bg-red-500/20' : 'bg-tiffany-500/20'
            }`}>
              {isAccepted ? (
                <DollarSign className="w-8 h-8 text-green-500" />
              ) : isDeclined ? (
                <X className="w-8 h-8 text-red-500" />
              ) : (
                <Clock className="w-8 h-8 text-tiffany-500" />
              )}
            </div>
            <h3 className="font-mono text-xl text-white">
              {isAccepted ? 'OFFER ACCEPTED!' : isDeclined ? 'OFFER ENDED' : 'REVIEWING YOUR OFFER'}
            </h3>
            <p className="font-mono text-sm text-zinc-400">
              {isAccepted ? (
                <>Your offer of <span className="text-green-500">${existingOffer.offer_amount?.toFixed(2)}</span> was accepted! Check your orders.</>
              ) : isDeclined ? (
                <>Offer negotiations have ended for this item.</>
              ) : existingOffer ? (
                <>Your offer of <span className="text-tiffany-500">${existingOffer.offer_amount?.toFixed(2)}</span> is being reviewed.</>
              ) : (
                <>Your offer has been submitted!</>
              )}
            </p>
            <p className="font-mono text-xs text-zinc-500">
              {isAccepted ? 'Complete checkout to secure your purchase!' : isDeclined ? 'You cannot make new offers on this item.' : 'Check your profile for updates!'}
            </p>
            <Button
              onClick={handleClose}
              className={`w-full mt-4 rounded-none font-mono ${
                isAccepted 
                  ? 'bg-green-500 hover:bg-green-400 text-black' 
                  : 'bg-zinc-800 hover:bg-zinc-700 text-white'
              }`}
            >
              {isAccepted ? 'VIEW ORDERS' : 'GOT IT'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const percentOptions = [75, 80, 85, 90, 95, 99];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md bg-zinc-950 border-zinc-800 p-0 max-h-[80vh] overflow-y-auto [&>button]:absolute [&>button]:right-2 [&>button]:top-2 [&>button]:p-1.5 [&>button]:bg-zinc-800 [&>button]:rounded [&>button]:opacity-100 [&>button]:hover:bg-zinc-700">

        <DialogHeader className="px-3 pt-3 pb-2 border-b border-zinc-800 pr-10">
          <DialogTitle className="font-mono text-base text-white flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-tiffany-500" />
            MAKE AN OFFER
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-3 space-y-2">
          {/* Product Info */}
          {(() => {
            let coverImage = product?.image_url;
            if (selectedVariant) {
              if (selectedVariant.image_url) {
                coverImage = selectedVariant.image_url;
              } else if (selectedVariant.image_indices?.length > 0 && product?.images?.length) {
                const idx = selectedVariant.image_indices[0];
                if (typeof idx === 'number' && idx >= 0 && idx < product.images.length) {
                  coverImage = product.images[idx];
                }
              } else if (typeof selectedVariant.image_index === 'number' && product?.images?.length) {
                const idx = selectedVariant.image_index;
                if (idx >= 0 && idx < product.images.length) {
                  coverImage = product.images[idx];
                }
              }
            }
            return (
              <div className="flex gap-2 p-2 bg-zinc-900 border border-zinc-800">
                {coverImage && (
                  <img 
                    src={coverImage.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${coverImage}` : coverImage}
                    alt={product?.name}
                    className="w-12 h-12 object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs text-white truncate">{product?.name}</p>
                  {selectedVariant?.name && (
                    <p className="font-mono text-[10px] text-purple-400 truncate">{selectedVariant.name}</p>
                  )}
                  <p className="font-mono text-base text-tiffany-500 font-bold">
                    ${effectivePrice.toFixed(2)}
                  </p>
                  <p className="font-mono text-[9px] text-zinc-500">ASKING PRICE</p>
                </div>
              </div>
            );
          })()}

          {/* Offer Amount Display */}
          <div className="text-center py-2 bg-zinc-900 border border-zinc-800">
            <p className="font-mono text-[9px] text-zinc-500 mb-0.5">YOUR OFFER</p>
            <p className="font-mono text-xl text-tiffany-500 font-bold" data-testid="offer-amount-display">
              ${offerAmount.toFixed(2)}
            </p>
            {!useCustom && (
              <p className="font-mono text-xs text-zinc-400">
                {offerPercent}% of asking price
              </p>
            )}
          </div>

          {/* Slider or Custom Input Toggle */}
          {!useCustom ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-mono text-[10px] text-zinc-500">75%</span>
                <span className="font-mono text-[10px] text-zinc-400">SELECT PERCENTAGE</span>
                <span className="font-mono text-[10px] text-zinc-500">99%</span>
              </div>
              
              <Slider
                value={[offerPercent]}
                onValueChange={(val) => setOfferPercent(val[0])}
                min={75}
                max={99}
                step={1}
                className="w-full"
                data-testid="offer-slider"
              />

              {/* Quick Select Buttons */}
              <div className="flex justify-between">
                {percentOptions.map((pct) => (
                  <button
                    key={pct}
                    onClick={() => setOfferPercent(pct)}
                    className={`font-mono text-[10px] px-1.5 py-0.5 transition-all ${
                      offerPercent === pct 
                        ? 'text-tiffany-500 bg-tiffany-500/20 border border-tiffany-500/50' 
                        : 'text-zinc-500 hover:text-zinc-300'
                    }`}
                    data-testid={`offer-percent-${pct}`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>

              <button
                onClick={() => setUseCustom(true)}
                className="w-full font-mono text-[10px] text-zinc-500 hover:text-tiffany-500 py-1"
              >
                ENTER CUSTOM AMOUNT
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="font-mono text-[10px] text-zinc-400">CUSTOM OFFER AMOUNT</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={minOffer.toFixed(2)}
                  min={minOffer}
                  step="0.01"
                  className="w-full pl-8 pr-4 py-2 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm focus:border-tiffany-500 outline-none"
                  autoFocus
                />
              </div>
              <p className="font-mono text-[9px] text-zinc-500">
                Minimum: ${minOffer.toFixed(2)} (75%)
              </p>
              <button
                onClick={() => { setUseCustom(false); setCustomAmount(''); }}
                className="w-full font-mono text-[10px] text-zinc-500 hover:text-tiffany-500 py-1"
              >
                USE SLIDER INSTEAD
              </button>
            </div>
          )}

          {/* Savings Preview */}
          {savings > 0 && (
            <div className="p-2 bg-tiffany-500/10 border border-tiffany-500/30">
              <div className="flex justify-between items-center">
                <span className="font-mono text-[10px] text-zinc-400">YOUR SAVINGS</span>
                <span className="font-mono text-sm text-tiffany-500 font-bold">
                  ${savings.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-mono text-[9px] text-zinc-500">DISCOUNT</span>
                <span className="font-mono text-xs text-tiffany-400">
                  {discountPercent.toFixed(0)}% OFF
                </span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 text-red-400">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              <span className="font-mono text-[10px]">{error}</span>
            </div>
          )}

          {/* Info */}
          <p className="font-mono text-[9px] text-zinc-500 text-center">
            OFFERS ARE REVIEWED MANUALLY. WE'LL NOTIFY YOU.
          </p>
        </div>
        
        <DialogFooter className="px-3 py-2 border-t border-zinc-800 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="flex-1 rounded-none border-zinc-700 text-zinc-400 hover:bg-zinc-800 font-mono h-9 text-xs"
          >
            <X className="w-3 h-3 mr-1" />
            CANCEL
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || offerAmount < minOffer}
            className="flex-1 rounded-none bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono h-9 text-xs"
            data-testid="submit-offer-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
